import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { useEffect } from "react";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { LanguageProvider } from "./context/LanguageContext";
import { Toaster } from "./components/ui/sonner";
import { trackPageView } from "./utils/consent";

// Pages
import LandingPage from "./pages/LandingPage";
import AuthPage from "./pages/AuthPage";
import Dashboard from "./pages/Dashboard";
import AddDebit from "./pages/AddDebit";
import EditDebit from "./pages/EditDebit";
import AllDebits from "./pages/AllDebits";
import UpgradePage from "./pages/UpgradePage";
import UpgradeSuccess from "./pages/UpgradeSuccess";
import SettingsPage from "./pages/SettingsPage";
import FinancialSupport from "./pages/FinancialSupport";
import FeedbackPage from "./pages/FeedbackPage";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import SharePage from "./pages/SharePage";
import AdminPage from "./pages/AdminPage";
import AdminLogin from "./pages/AdminLogin";
import CookiePolicy from "./pages/CookiePolicy";
import ConsentBanner from "./components/ConsentBanner";
import BlogListPage from "./pages/BlogListPage";
import BlogPostPage from "./pages/BlogPostPage";
import AdminBlogPage from "./pages/AdminBlogPage";
import AdminBlogAISettingsPage from "./pages/AdminBlogAISettingsPage";
import AdminPromoPage from "./pages/AdminPromoPage";
import AdminTechSpecPage from "./pages/AdminTechSpecPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import HowItWorksPage from "./pages/HowItWorksPage";

// Admin emails for frontend check
const ADMIN_EMAILS = [
  "diane.shawe@yahoo.com",
  "admin@mydirectdebits.com",
  "support@qualiquiz.app",
  "info@virtualautochat.uk"
];

// Protected Route Component
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-page flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  return children;
}

// Public Route (redirect to dashboard if logged in)
function PublicRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-page flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

// Admin Protected Route - only for founder emails
function AdminRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-page flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Check if user is logged in and is an admin
  const isAdmin = user && ADMIN_EMAILS.includes(user.email?.toLowerCase());
  
  if (!isAdmin) {
    return <Navigate to="/admin/login" replace />;
  }

  return children;
}

function AppRoutes() {
  const location = useLocation();
  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location.pathname, location.search]);

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/auth" element={<PublicRoute><AuthPage /></PublicRoute>} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/cookies" element={<CookiePolicy />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/how-it-works" element={<HowItWorksPage />} />
      <Route path="/blog" element={<BlogListPage />} />
      <Route path="/blog/:slug" element={<BlogPostPage />} />

      {/* Admin login - separate from user auth */}
      <Route path="/admin/login" element={<AdminLogin />} />

      {/* Protected routes */}
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/add" element={<ProtectedRoute><AddDebit /></ProtectedRoute>} />
      <Route path="/edit/:id" element={<ProtectedRoute><EditDebit /></ProtectedRoute>} />
      <Route path="/debits" element={<ProtectedRoute><AllDebits /></ProtectedRoute>} />
      <Route path="/upgrade" element={<ProtectedRoute><UpgradePage /></ProtectedRoute>} />
      <Route path="/upgrade/success" element={<ProtectedRoute><UpgradeSuccess /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      <Route path="/support" element={<ProtectedRoute><FinancialSupport /></ProtectedRoute>} />
      <Route path="/feedback" element={<ProtectedRoute><FeedbackPage /></ProtectedRoute>} />
      
      {/* Admin only routes */}
      <Route path="/share" element={<AdminRoute><SharePage /></AdminRoute>} />
      <Route path="/admin" element={<AdminRoute><AdminPage /></AdminRoute>} />
      <Route path="/admin/blog" element={<AdminRoute><AdminBlogPage /></AdminRoute>} />
      <Route path="/admin/blog/ai-settings" element={<AdminRoute><AdminBlogAISettingsPage /></AdminRoute>} />
      <Route path="/admin/promos" element={<AdminRoute><AdminPromoPage /></AdminRoute>} />
      <Route path="/admin/technical-spec" element={<AdminRoute><AdminTechSpecPage /></AdminRoute>} />

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <LanguageProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster position="top-center" richColors />
          <ConsentBanner />
        </BrowserRouter>
        </LanguageProvider>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
